import { CITATION_SOURCES } from '../mock/data.ts';
import type { SourceId } from '../types.ts';

// Pill component used wherever a source is quoted. 7 registered sources, each
// with a hue drawn from a single mid-saturation palette. New sources require
// expanding CITATION_SOURCES — do not invent ad-hoc colors.
export function CitationBadge({
  source,
  suffix,
  href,
}: {
  source: SourceId;
  suffix?: string;
  href?: string;
}) {
  // Defensive: if the agent fabricates a source string the renderer used to
  // crash here when `meta` was undefined. Drop the pill entirely instead of
  // rendering a broken badge or throwing — the caller should already have
  // validated the source upstream, but never trust the LLM's output shape.
  const meta = CITATION_SOURCES[source];
  if (!meta) return null;
  // Cap suffix length defensively — the LLM cannot pad a real source with a
  // fake authority blurb like "p<0.001, n=12,847" to dress up a fabrication.
  const safeSuffix = suffix ? suffix.slice(0, 32) : suffix;
  const Tag = href ? 'a' : 'span';
  return (
    <Tag
      {...(href ? { href, target: '_blank', rel: 'noreferrer noopener' } : {})}
      className="caps mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 7px',
        background: `${meta.hue}1f`,
        border: `1px solid ${meta.hue}55`,
        borderRadius: 999,
        color: meta.textHue,
        fontSize: 9.5,
        letterSpacing: '0.08em',
        cursor: href ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        className="dot"
        style={{ background: meta.hue, width: 5, height: 5 }}
        aria-hidden="true"
      />
      {meta.label}
      {safeSuffix ? (
        <span style={{ color: 'var(--fg-3)', marginLeft: 2 }}>· {safeSuffix}</span>
      ) : null}
    </Tag>
  );
}
